<template>
  <v-app>
    <template v-if="ready">
      <router-view></router-view>
    </template>
    <template v-else>
      <div class="loading">
        <div
          class="d-flex min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
        >
          <div class="max-w-md w-full space-y-8">
            <v-loader-animation
              :text="'text-black'"
              :loading-text-style="'text-black text-lg'"
              loading-text="loading"
            />
          </div>
        </div>
      </div>
    </template>
  </v-app>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import axios from "@/services/ApiConnection";
import AppSyncClient from '@/services/subscribe/providers/Connection'
import { useRouter } from "vue-router";
import { useAuth } from "@/services/user/Auth0UserProvider";
import vLoaderAnimation from "@/components/template/v-loader-animation.vue";
import Api from "@/services/api/ApiServiceFabrick";
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi";
import { rollbar } from "@/plugins/rollbar.js";

export default defineComponent({
  name: "App",
  setup() {
    const { initializeAuth } = useAuth();
    const ready = ref(false);
    const { push } = useRouter();
    let showPageAfterRedirect = true;

    initializeAuth(
      {
        domain: process.env.VUE_APP_AUTH0_DOMAIN ?? '',
        client_id: process.env.VUE_APP_AUTH0_CLIENTID ?? '',
        audience: process.env.VUE_APP_AUTH0_AUDIENCE ?? '',
        useRefreshTokens: true,
      },
      (appState) => {
        if (appState && appState.targetUrl) {
          push(appState.targetUrl).then(() => (ready.value = true));
        }
      },
      () => (showPageAfterRedirect = false)
    ).then(async () => {
      const userInfo = useAuth();
      if (userInfo.isAuthenticated.value) {
        // tokenを取得
        const token = await userInfo.getTokenSilently();
        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
        AppSyncClient.token = token

        Api()
          .getAuthority()
          .then((data) => {
            AuthorityCacheApi.put(data);
          })
          .catch((error) => {
            AuthorityCacheApi.put({});
            showPageAfterRedirect = true;
            ready.value = true;
            switch (error.response.status) {
              case 401:
              case 403:
                push("/forbidden");
                break;
            }
          })
          .finally(async () => {
            if (!showPageAfterRedirect) {
              // add account in cache here
              // to prevent showing white screen
              // after loading animation
              const selfAccount = await Api().getSelfAccount();
              rollbar.configure({
                payload: {
                  person: {
                    id: selfAccount.id,
                    email: selfAccount.email ?? '',
                    username: selfAccount.full_name,
                  },
                  custom: {
                    officeable_type: selfAccount.officeable_type,
                    officeable_id: selfAccount.officeable_id,
                    login_organization_id: selfAccount.login_organization_id ?? '',
                    login_organization_type: selfAccount.login_organization_type ?? '',
                    department_id: selfAccount.department_id ?? '',
                  },
                },
              });
              ready.value = true;
            }
          });
      } else {
        ready.value = true;
      }
    });

    return {
      ready,
    };
  },
  components: {
    vLoaderAnimation,
  },
});
</script>
<style lang="scss">
:root {
  --list-default-height: 544px;
}
.vue-notifyjs.notifications {
  .list-move {
    transition: transform 0.3s, opacity 0.4s;
  }
  .list-item {
    display: inline-block;
    margin-right: 10px;
  }
  .list-enter-active {
    transition: transform 0.2s ease-in, opacity 0.4s ease-in;
  }
  .list-leave-active {
    transition: transform 1s ease-out, opacity 0.4s ease-out;
  }

  .list-enter {
    opacity: 0;
    transform: scale(1.1);
  }
  .list-leave-to {
    opacity: 0;
    transform: scale(1.2, 0.7);
  }
}
.list-scrollbar::-webkit-scrollbar,
.table-scrollbar .v-table__wrapper::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}
.list-scrollbar::-webkit-scrollbar-thumb,
.table-scrollbar .v-table__wrapper::-webkit-scrollbar-thumb {
  background-color: rgb(150, 150, 150);
  border-radius: 5px;
  box-shadow: inset 3px 3px 3px rgba(255, 255, 255, 0.2);
}
.list-scrollbar::-webkit-scrollbar-track,
.table-scrollbar .v-table__wrapper::-webkit-scrollbar-track {
  background-color: rgb(220, 220, 220);
  border-radius: 5px;
  box-shadow: inset 3px -3px 5px rgba(0, 0, 0, 0.2);
}
.not-searched {
  height: 100%;
  position: absolute;
  z-index: 1000;
  background-color: rgba(80,80,80,0.5);
  right: 0;
  left: 0;
}
.not-searched>label {
  font-weight: bold;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
}
.gm-style-iw-chr {
  height: 10px;
}
.gm-style-iw-ch {
  height: 0;
}
.gm-style-iw-d {
  padding-right: 20px;
}
</style>
